import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Search, Book, Code, Layers, Settings, Users } from 'lucide-react'
import { getPopularArticles, getRecentArticles, getAllCategories, featuredCategories, searchArticles } from '@/data/helpData'
import ArticleCard from './components/ArticleCard'
import CategoryFilter from './components/CategoryFilter'
import CategoryTopicsModal from './components/CategoryTopicsModal'
import type { InteractiveFeaturedCategory } from '@/data/helpData'

export default function HelpHome () {
  const [selectedCategories, setSelectedCategories] = useState<string[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<InteractiveFeaturedCategory | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const popularArticles = getPopularArticles(4)
  const recentArticles = getRecentArticles(4)
  const categories = getAllCategories().map(cat => ({
    id: cat.toLowerCase(),
    name: cat,
    count: searchArticles('').filter(article => article.category.toLowerCase() === cat.toLowerCase()).length
  }))

  const toggleCategory = (categoryId: string) => {
    setSelectedCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    )
  }

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value.toLowerCase())
  }

  const handleCategoryClick = (category: InteractiveFeaturedCategory) => {
    if (category.topics && category.topics.length > 0) {
      setSelectedCategory(category)
      setIsModalOpen(true)
    }
  }

  const filterArticles = (articlesToFilter: typeof popularArticles) => {
    return articlesToFilter.filter(article => {
      const categoryMatch = selectedCategories.length === 0 ||
        selectedCategories.includes(article.category.toLowerCase())
      const searchMatch = !searchTerm ||
        article.title.toLowerCase().includes(searchTerm) ||
        article.excerpt.toLowerCase().includes(searchTerm)
      return categoryMatch && searchMatch
    })
  }

  const filteredPopularArticles = useMemo(() => filterArticles(popularArticles), [searchTerm, selectedCategories, popularArticles])
  const filteredRecentArticles = useMemo(() => filterArticles(recentArticles), [searchTerm, selectedCategories, recentArticles])

  const getIcon = (iconName: string) => {
    const iconProps = { className: 'h-12 w-12 text-purple-600 dark:text-purple-400' }
    switch (iconName) {
      case 'book': return <Book {...iconProps} />
      case 'code': return <Code {...iconProps} />
      case 'layers': return <Layers {...iconProps} />
      case 'settings': return <Settings {...iconProps} />
      case 'users': return <Users {...iconProps} />
      default: return <Book {...iconProps} />
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-purple-700 to-fuchsia-700 dark:from-purple-800 dark:to-fuchsia-900 py-20 md:py-28">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl font-extrabold text-white mb-6 leading-tight">Help & Support</h1>
            <p className="text-lg sm:text-xl text-white dark:text-purple-200 mb-10">How can we help you today?</p>
            <div className="relative max-w-2xl mx-auto">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-purple-400" />
              </div>
              <input
                type="text"
                placeholder="Search for articles, tutorials, and more..."
                value={searchTerm}
                onChange={handleSearch}
                className="pl-12 pr-4 py-3.5 bg-white dark:bg-gray-800 border-2 border-transparent focus:border-purple-300 dark:focus:border-purple-600 focus:ring-4 focus:ring-purple-500/30 rounded-xl w-full dark:text-white text-base shadow-lg transition-all duration-200"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Featured Categories */}
      <section className="container mx-auto px-4 py-16 md:py-20">
        <h2 className="text-3xl font-bold text-purple-900 dark:text-purple-300 mb-12 text-center">What do you need help with?</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {featuredCategories.map((cat) => {
            const CategoryWrapper = cat.topics && cat.topics.length > 0 ? 'button' : Link;
            const wrapperProps = cat.topics && cat.topics.length > 0 ? { onClick: () => handleCategoryClick(cat) } : { to: cat.link! };

            return (
              <CategoryWrapper
                key={cat.name}
                {...wrapperProps}
                className="group block p-6 md:p-8 bg-white dark:bg-gray-800/70 border border-gray-200 dark:border-gray-700/80 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 ease-in-out transform hover:-translate-y-1.5 w-full">
                <div className="flex flex-col items-center text-center">
                  <div className="mb-5 p-3 bg-purple-100 dark:bg-purple-900/40 rounded-full group-hover:bg-purple-200 dark:group-hover:bg-purple-800/60 transition-colors duration-300">
                    {getIcon(cat.icon)}
                  </div>
                  <h3 className="text-xl font-semibold text-purple-800 dark:text-purple-300 mb-2 group-hover:text-purple-600 dark:group-hover:text-purple-200 transition-colors duration-300">
                    {cat.name}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{cat.description}</p>
                  {cat.topics && cat.topics.length > 0 && (
                    <div className="mt-3 text-xs text-purple-600 dark:text-purple-400 font-medium">
                      {cat.topics.length} common questions →
                    </div>
                  )}
                </div>
              </CategoryWrapper>
            );
          })}
        </div>
      </section>

      {/* Articles Section */}
      <section className="container mx-auto px-4 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar Filter */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 bg-white dark:bg-gray-900 p-6 rounded-xl shadow-lg border border-gray-200 dark:border-gray-800">
              <h2 className="text-xl font-semibold text-purple-900 dark:text-purple-300 mb-5">Filter Articles</h2>
              <CategoryFilter categories={categories} selectedCategories={selectedCategories} onCategoryToggle={toggleCategory} />
              <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-medium text-purple-900 dark:text-purple-300 mb-3">Need more help?</h3>
                <p className="text-sm text-purple-700 dark:text-purple-300 mb-4">
                  Can't find what you're looking for? Our support team is here to help.
                </p>
                <button className="w-full inline-flex items-center justify-center rounded-lg text-sm font-medium h-10 px-4 py-2 bg-purple-600 text-purple-50 hover:bg-purple-700 dark:bg-purple-700 dark:hover:bg-purple-600 transition-colors">
                  Contact Support
                </button>
              </div>
            </div>
          </div>

          {/* Articles Content */}
          <div className="lg:col-span-3">
            {/* Popular Articles */}
            <div className="mb-12">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-semibold text-purple-900 dark:text-purple-300">Popular Articles</h2>
                <Link
                  to="/help/articles"
                  className="text-sm font-medium text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300 transition-colors">
                  View all
                </Link>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {filteredPopularArticles.length > 0 ? (
                  filteredPopularArticles.map((article) => <ArticleCard key={article.id} article={article} />)
                ) : (
                  <p className="text-gray-500 dark:text-gray-400 md:col-span-2 text-center py-4">
                    No popular articles match your criteria.
                  </p>
                )}
              </div>
            </div>

            {/* Recent Articles */}
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-semibold text-purple-900 dark:text-purple-300">Recent Articles</h2>
                <Link
                  to="/help/articles"
                  className="text-sm font-medium text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300 transition-colors">
                  View all
                </Link>
              </div>
              <div className="space-y-6">
                {filteredRecentArticles.length > 0 ? (
                  filteredRecentArticles.map((article) => (
                    <div
                      key={article.id}
                      className="p-6 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 ease-in-out transform hover:-translate-y-1">
                      <Link to={`/help/articles/${article.slug}`} className="group">
                        <h3 className="text-lg font-semibold text-purple-900 dark:text-purple-300 group-hover:text-purple-700 dark:group-hover:text-purple-400 mb-2 transition-colors">
                          {article.title}
                        </h3>
                      </Link>
                      <p className="text-gray-600 dark:text-gray-400 mb-4 text-sm">{article.excerpt}</p>
                      <div className="flex items-center justify-between text-xs">
                        <span className="inline-flex items-center px-3 py-1 rounded-full font-medium bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-300">
                          {article.category}
                        </span>
                        <span className="text-gray-500 dark:text-gray-500">{article.date}</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 dark:text-gray-400 text-center py-4">No recent articles match your criteria.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Category Topics Modal */}
      {selectedCategory && (
        <CategoryTopicsModal
          category={selectedCategory}
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedCategory(null);
          }}
        />
      )}
    </div>
  );
}
